import { ReactElement, useState } from 'react'
import { useInterval } from 'react-use'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { CircularProgress, Divider, Stack, Typography } from '@mui/material'
import {
  getListGrantsQueryKey,
  useCreateGrant,
  useExtendGrant,
  useListGrants,
  useRevokeGrant,
} from '@api/ai-access/ai-access'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import GrantRow from './GrantRow'
import GrantSelector from './GrantSelector'
import McpConnectionInfo from './McpConnectionInfo'
import { isExpired } from './utils'

// Keep in sync with the backend's EXTEND_MINUTES (use_cases/ai_access).
const EXTEND_MINUTES = 60
const COUNTDOWN_TICK_MS = 10_000

/** The "Verbindung" tab: manage grants and show the MCP connection info. */
const ConnectionTab = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const [nowMs, setNowMs] = useState(() => Date.now())
  useInterval(() => setNowMs(Date.now()), COUNTDOWN_TICK_MS)

  const { data, isLoading, isError } = useListGrants({ query: { refetchInterval: 60_000 } })
  const grants = (data?.grants ?? []).filter(grant => !isExpired(grant.expires_at, nowMs))

  const invalidateGrants = () =>
    queryClient.invalidateQueries({ queryKey: getListGrantsQueryKey() })

  const handleError = (fallback: string) => (error: AxiosError) => {
    enqueueSnackbar(buildErrorMessage(error, fallback), { variant: 'error' })
  }

  const { mutate: createGrant, isLoading: isCreating } = useCreateGrant<AxiosError>({
    mutation: {
      onSuccess: async () => {
        await invalidateGrants()
        enqueueSnackbar('Freigabe erteilt', { variant: 'success' })
      },
      onError: handleError('Fehler beim Erteilen der Freigabe'),
    },
  })

  const { mutate: extendGrant, isLoading: isExtending } = useExtendGrant<AxiosError>({
    mutation: {
      onSuccess: () => invalidateGrants(),
      onError: handleError('Fehler beim Verlängern der Freigabe'),
    },
  })

  const { mutate: revokeGrant, isLoading: isRevoking } = useRevokeGrant<AxiosError>({
    mutation: {
      onSuccess: () => invalidateGrants(),
      onError: handleError('Fehler beim Widerrufen der Freigabe'),
    },
  })

  const isBusy = isCreating || isExtending || isRevoking

  return (
    <Stack spacing={1.5} data-cy="ai-access-connection-tab">
      <Stack spacing={1}>
        <Typography variant="caption" color="text.secondary">
          Aktive Freigaben
        </Typography>
        {isLoading && (
          <Stack alignItems="center" py={1}>
            <CircularProgress size={20} />
          </Stack>
        )}
        {isError && (
          <Typography variant="body2" color="error" data-cy="ai-access-grants-error">
            Freigaben konnten nicht geladen werden.
          </Typography>
        )}
        {!isLoading && !isError && grants.length === 0 && (
          <Typography variant="body2" color="text.secondary" data-cy="ai-access-no-grants">
            Keine aktiven Freigaben — die KI hat keinen Zugriff.
          </Typography>
        )}
        {grants.map(grant => (
          <GrantRow
            key={grant.id}
            grant={grant}
            nowMs={nowMs}
            onExtend={grantId => extendGrant({ grantId, data: { minutes: EXTEND_MINUTES } })}
            onRevoke={grantId => revokeGrant({ grantId })}
            isBusy={isBusy}
          />
        ))}
      </Stack>
      <GrantSelector
        onGrantProject={projectGuid =>
          createGrant({ data: { resource_type: 'project', project_guid: projectGuid } })
        }
        onGrantOrgCapability={resourceType =>
          createGrant({ data: { resource_type: resourceType } })
        }
        isBusy={isBusy}
      />
      <Divider />
      <McpConnectionInfo />
      <Typography variant="caption" color="text.secondary">
        Die KI sieht ausschließlich die oben freigegebenen Projekte, und alles was sie ändert, wird
        als „(ai)“ gekennzeichnet.
      </Typography>
    </Stack>
  )
}

export default ConnectionTab
